import { Outlet, Navigate, useLocation } from 'react-router';
import { useLogin } from '../../hooks/useLogin';
import { Loader } from '@f2m3/shared-frontend-ui';
import { useVerificationWall } from '../../hooks/useVerificationWall';
import { TOKEN_KEY, GUEST_TOKEN_KEY } from '../../api/queryHooks/user';

/**
 * Route guard that prevents guest users from accessing checkout steps.
 * Guest users are redirected to login, with their token saved for later cart transfer;
 * a user who still has to confirm their email goes to the code page.
 */
const GuestCheckoutGuard = () => {
  const { isLoggedIn, isGuest, isPending, isFetching } = useLogin();
  const { pending: verificationPending, ready: verificationReady } = useVerificationWall();
  const location = useLocation();

  // A refetch in flight means the cached "not logged in" may be stale (a token
  // just arrived — see useLogin.isFetching); wait for it rather than redirect.
  if (isPending || (isFetching && !isLoggedIn)) {
    return <Loader />;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  if (isGuest) {
    // Save guest token before redirecting to login
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      localStorage.setItem(GUEST_TOKEN_KEY, currentToken);
    }
    // Clear the regular token so the user appears logged out
    localStorage.removeItem(TOKEN_KEY);
    return <Navigate to="/login" state={{ from: location.pathname, guestCheckout: true }} />;
  }

  if (!verificationReady) return <Loader />;
  if (verificationPending) {
    return <Navigate to="/verify-email/code" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
};

export default GuestCheckoutGuard;
